/**
 * EMP-WORK-EMAIL-CHANGE-001 — two-step Work Email change dialog (Admin / HR Manager).
 * Updates the HR record only; login (users.email) and linked_user_id are never touched.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const WorkEmailChangeDialog = ({ open, employee, onClose, onChanged }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [ctx, setCtx] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const timer = useRef(null); const idemKey = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(1); setEmail(''); setReason(''); setConflict(null); setApiError(null); setCtx(null);
    idemKey.current = `wemail-${employee?.employee_id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    axios.get(`${API}/api/hr/employees/${employee.employee_id}/work-email-context`, { headers: authHeaders() }).then(r => setCtx(r.data)).catch(() => setCtx({}));
  }, [open, employee?.employee_id]);

  const norm = email.trim().toLowerCase();
  const current = (employee?.work_email || '').toLowerCase();
  const formatOk = EMAIL_RE.test(norm);
  const same = !!norm && norm === current;
  const onEmailChange = (v) => {
    setEmail(v); setConflict(null); setApiError(null); clearTimeout(timer.current);
    const n = v.trim().toLowerCase();
    if (!EMAIL_RE.test(n) || n === current) return;
    timer.current = setTimeout(async () => {
      setChecking(true);
      try { const r = await axios.get(`${API}/api/hr/employees/${employee.employee_id}/work-email-context`, { headers: authHeaders(), params: { new_work_email: n } }); setConflict(r.data.conflict || null); }
      catch { /* backend re-validates on save */ } finally { setChecking(false); }
    }, 400);
  };
  const valid = formatOk && !same && reason.trim().length >= 5 && !conflict && !checking;
  const loginEmail = ctx?.linked_login_email;
  const differs = !!loginEmail && loginEmail.toLowerCase() !== norm;

  const save = async () => {
    setSaving(true); setApiError(null);
    try {
      const r = await axios.post(`${API}/api/hr/employees/${employee.employee_id}/change-work-email`, { new_work_email: norm, reason: reason.trim(), idempotency_key: idemKey.current }, { headers: authHeaders() });
      toast.success(`Work email changed: ${r.data.previous_work_email} → ${r.data.new_work_email}`, { description: `${r.data.employee_name} — login unchanged` });
      onChanged(r.data.new_work_email);
    } catch (e) { setApiError(e.response?.data?.detail || 'Failed to change work email'); setStep(1); }
    finally { setSaving(false); }
  };

  if (!employee) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="work-email-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" />{step === 1 ? 'Edit Work Email' : `Change work email for ${employee.full_name}?`}</DialogTitle>
          <DialogDescription className="sr-only">Change the HR work email with a mandatory reason and confirmation</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="space-y-4">
            <div><label className="text-xs font-medium text-slate-600 block mb-1">Current Work Email</label><Input value={employee.work_email || ''} disabled className="h-9 bg-slate-50" data-testid="wemail-current-input" /></div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="wemail-new">New Work Email <span className="text-rose-500">*</span></label>
              <Input id="wemail-new" type="email" value={email} onChange={(e) => onEmailChange(e.target.value)} placeholder="name@company.com" className={`h-9 ${conflict || (norm && !formatOk) ? 'border-rose-300' : ''}`} data-testid="wemail-new-input" />
              {checking && <p className="text-[11px] text-slate-400 mt-1">Checking availability…</p>}
              {norm && !formatOk && <p className="text-[11px] text-rose-600 mt-1" data-testid="wemail-format-error">Enter a valid email address.</p>}
              {same && <p className="text-[11px] text-rose-600 mt-1" data-testid="wemail-same-error">The new work email must be different from the current one.</p>}
              {conflict && <p className="text-[11px] text-rose-600 mt-1" data-testid="wemail-conflict-error">{conflict}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="wemail-reason">Reason for change <span className="text-rose-500">*</span></label>
              <Textarea id="wemail-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Typo in the work email entered at onboarding (min 5 characters)" rows={2} maxLength={500} className="text-sm resize-none" data-testid="wemail-reason-input" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2 text-xs text-slate-600" data-testid="wemail-login-note">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <p><span className="font-medium text-slate-700">Linked login email:</span> {ctx ? (loginEmail || 'No linked login account') : '…'}<br />Login/credentials are not affected. If the login email must also be changed, it must be handled separately through Admin › Users.</p>
            </div>
            {apiError && <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="wemail-api-error">{apiError}</p>}
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-slate-700" data-testid="wemail-confirm-summary">
              <p><span className="text-slate-400">Employee:</span> <strong>{employee.full_name}</strong></p>
              <p><span className="text-slate-400">Current Work Email:</span> <strong data-testid="wemail-confirm-current">{employee.work_email}</strong></p>
              <p><span className="text-slate-400">New Work Email:</span> <strong className="text-blue-700" data-testid="wemail-confirm-new">{norm}</strong></p>
              <p><span className="text-slate-400">Linked Login Email:</span> <strong data-testid="wemail-confirm-login">{loginEmail || 'No linked login account'}</strong></p>
              <p><span className="text-slate-400">Reason:</span> {reason.trim()}</p>
            </div>
            <p className="text-xs text-slate-600" data-testid="wemail-confirm-note">Login/credentials are not affected. If the login email must also be changed, it must be handled separately through Admin › Users.</p>
            {differs && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="wemail-differs-warning">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">The new work email will differ from the linked login email ({loginEmail}). Email-based fallbacks (attendance report mapping, leave, celebrations, IT assets) keep resolving through the existing account link, which stays intact.</p>
              </div>
            )}
            <p className="text-xs text-slate-500">This change is audited and takes effect immediately after saving.</p>
          </div>
        )}
        <DialogFooter>
          {step === 1 ? (<>
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="wemail-cancel-btn">Cancel</Button>
            <Button onClick={() => setStep(2)} disabled={!valid} data-testid="wemail-continue-btn">Continue</Button>
          </>) : (<>
            <Button variant="outline" onClick={() => setStep(1)} disabled={saving} data-testid="wemail-back-btn">Back</Button>
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="wemail-confirm-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Confirm Change</Button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkEmailChangeDialog;
